import { useCallback, useState } from 'react';
import { usePermissionChangeHandler } from '@lp/features/RoutePlanner/hooks/usePermissionChangeHandler';
import { useRoleRestrictions } from '@/hooks/useRoleRestrictions';
import { CustomerPermissionOnly, CustomerRoleOnly } from '@/api/apis/customer';

type UseRoleFormLogicProps = {
  roleValues: CustomerRoleOnly[];
  permissionValues: CustomerPermissionOnly[];
  updateRoles: (roles: CustomerRoleOnly[]) => void;
  updatePermissions: (perms: CustomerPermissionOnly[]) => void;
  triggerBoth: () => Promise<[boolean, boolean]>;
};

export const useRoleFormLogic = ({
  roleValues,
  permissionValues,
  updateRoles,
  updatePermissions,
  triggerBoth,
}: UseRoleFormLogicProps) => {
  const [permissionRequiredModalData, setPermissionRequiredModalData] = useState<{
    scenario: 'cannotRemove' | 'cannotAdd';
    roleNames: string[];
    permissionName: string;
  } | null>(null);

  const {
    isPermissionMandatoryForActiveRole,
    isPermissionSelectionForbiddenForRole,
    handleRoleChange: triggerRoleChange,
  } = useRoleRestrictions({
    roleValues,
    permissionValues,
    updateRoles,
    updatePermissions,
    trigger: triggerBoth,
  });

  const handleRoleChange = useCallback(
    (newRoles: string[]) => {
      triggerRoleChange(newRoles as CustomerRoleOnly[]);
    },
    [triggerRoleChange],
  );

  const handlePermissionChange = usePermissionChangeHandler({
    roleValues,
    permissionValues,
    isPermissionMandatoryForActiveRole,
    isPermissionSelectionForbiddenForRole,
    updatePermissions,
    setPermissionRequiredModalData,
  });

  return {
    handleRoleChange,
    handlePermissionChange,
    permissionRequiredModalData,
    setPermissionRequiredModalData,
  };
};
